/**
 * promo-badges.js
 * ──────────────────────────────────────────────────────────────────────────
 * Reads window.productBadges (array built server-side by sections/promo-badges.liquid)
 * and injects badge HTML into every .product-promo-badges[data-product-tags]
 * placeholder on the page — including cards rendered asynchronously by
 * SearchSpring on collection pages.
 *
 * Badge HTML rendered per matched promo:
 *   <a href="…" class="promo-badge js-promo-badge-trigger">
 *     [<img src="…">] <span>Grid Item Badge text</span>
 *   </a>
 * Multiple badges are joined with an "or" separator (inline, not stacked).
 *
 * Badge field priority for grid cards: badge.item → badge.title → badge.tag
 *
 * Usage: loaded via <script src="promo-badges.js" defer> inside
 *        sections/promo-badges.liquid so it is only present when the section
 *        is active.
 */

(function () {
  'use strict';

  /* Re-entry guard: the theme editor can re-inject this section's deferred
     script, which would otherwise stack a second MutationObserver. */
  if (window.__promoBadgesInit) {
    if (typeof window.injectPromoBadges === 'function') window.injectPromoBadges();
    return;
  }
  window.__promoBadgesInit = true;

  /* ── Helpers ─────────────────────────────────────────────────────────── */

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Build the inner HTML for a single badge anchor/span.
   * Uses badge.item (Grid Item Badge) as label, falling back to title then tag.
   */
  function buildBadgeHTML(badge) {
    // option badges store text in badge.label; others use item/title/tag
    var label = badge.label || badge.item || badge.title || badge.tag || '';
    var color = badge.colour || badge.color || '#006F29';
    var inner = '';

    // Resolve icon to show before text
    var iconSrc = '';
    if (badge.type === 'icon' || badge.type === 'graphic') {
      // Image Badge Map / Hover Image Map: icon field is already a full CDN URL
      iconSrc = badge.icon || '';
    } else {
      // text / option badges: always show promo-tag.svg
      iconSrc = window.promoTagIconUrl || '';
    }

    if (iconSrc) {
      inner += '<img src="' + esc(iconSrc) + '" alt="" aria-hidden="true" style="width:14px !important;height:14px !important;object-fit:contain !important;flex-shrink:0 !important;">';
    }
    inner += '<span>' + esc(label) + '</span>';

    if (badge.url) {
      var handle = badge.url.split('/').filter(Boolean).pop() || '';
      handle = handle.split('?')[0].split('#')[0];
      return (
        '<a href="' + esc(badge.url) + '"' +
        ' class="promo-badge js-promo-badge-trigger"' +
        ' data-promo-handle="' + esc(handle) + '"' +
        ' data-promo-title="' + esc(label) + '"' +
        ' style="color:' + esc(color) + ';display:inline-flex;align-items:center;gap:4px;font-size:11px;text-decoration:none;white-space:nowrap;">' +
        inner +
        '</a>'
      );
    }

    // No URL: still render as a clickable anchor pointing to the collection derived
    // from the badge tag. In cart/YMAL context product-promo-modal.js will intercept
    // the click and open the multi-buy popup instead of navigating.
    var tagHandle = (badge.tag || label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    var collectionUrl = root + 'collections/' + tagHandle;
    return (
      '<a href="' + esc(collectionUrl) + '"' +
      ' class="promo-badge js-promo-badge-trigger"' +
      ' data-promo-handle="' + esc(tagHandle) + '"' +
      ' data-promo-title="' + esc(label) + '"' +
      ' style="color:' + esc(color) + ';display:inline-flex;align-items:center;gap:4px;font-size:11px;text-decoration:none;white-space:nowrap;">' +
      inner +
      '</a>'
    );
  }

  /* ── Scheduling ──────────────────────────────────────────────────────── */

  var DEBOUNCE_MS = 250;
  var debounceTimer = null;
  // True while injectBadges writes to the DOM, so the observer below can ignore
  // the mutations injection causes itself.
  var isWriting = false;

  /** Coalesce a burst of mutations into a single injection run. */
  function schedule() {
    if (debounceTimer) return;
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      injectBadges();
    }, DEBOUNCE_MS);
  }

  /* ── Core injection ──────────────────────────────────────────────────── */

  /** Only touch inline style when it is not already in the desired state. */
  function setStyleIfNeeded(el, prop, value) {
    if (el.style[prop] !== value) el.style[prop] = value;
  }

  function injectBadges() {
    var badges = window.productBadges;
    if (!badges || !badges.length) return;

    var placeholders = document.querySelectorAll(
      '.product-promo-badges[data-product-tags]:not([data-badges-injected])'
    );
    if (!placeholders.length) return;

    isWriting = true;

    try {
      placeholders.forEach(function (el) {
        el.setAttribute('data-badges-injected', '');

        var rawTags = el.getAttribute('data-product-tags') || '';
        var tags = rawTags.split('||').map(function (t) { return t.trim(); }).filter(Boolean);

        var matched = badges.filter(function (b) {
          return b.tag && tags.indexOf(b.tag.trim()) !== -1;
        });

        if (!matched.length) {
          if (el.innerHTML !== '') el.innerHTML = '';
          setStyleIfNeeded(el, 'display', 'none');
          return;
        }

        var html = '';
        matched.forEach(function (badge, i) {
          if (i > 0) {
            html += '<span class="promo-badge-separator" style="font-size:14px;color:#006F29;"> or </span>';
          }
          html += buildBadgeHTML(badge);
        });

        if (el.innerHTML !== html) el.innerHTML = html;
        // Always use flex so inline display works for both block and snippet versions
        setStyleIfNeeded(el, 'display', 'flex');
        setStyleIfNeeded(el, 'flexWrap', 'nowrap');
        setStyleIfNeeded(el, 'alignItems', 'center');
        setStyleIfNeeded(el, 'gap', '4px');
        setStyleIfNeeded(el, 'overflow', 'visible');
      });
    } finally {
      isWriting = false;
    }
  }

  /* ── Boot ────────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectBadges);
  } else {
    injectBadges();
  }

  // Theme editor, plus the synthetic dispatch in header.js after a soft nav.
  document.addEventListener('shopify:section:load', schedule);

  // Cart drawer / cart page morphs swap in fresh, un-injected placeholders.
  document.addEventListener('cart:update', schedule);

  /* Body-wide + subtree is required because SearchSpring injects PLP grids client-side.
     Attribute observation is restricted to `data-product-tags` only: SearchSpring can
     reuse a card node for a different product and update that attribute without inserting
     a new placeholder. Watching exactly that signal prevents a stale badge without waking
     this callback for class/style/ARIA mutations elsewhere on the page. */
  var observer = new MutationObserver(function (mutations) {
    if (isWriting) return;

    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];

      if (
        m.type === 'attributes' &&
        m.target &&
        m.target.matches &&
        m.target.matches('.product-promo-badges[data-product-tags]')
      ) {
        m.target.removeAttribute('data-badges-injected');
        schedule();
        return;
      }

      if (m.target && m.target.closest && m.target.closest('.product-promo-badges')) continue;

      var added = m.addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (
          (node.classList && node.classList.contains('product-promo-badges') && node.hasAttribute('data-product-tags')) ||
          (node.querySelector && node.querySelector('.product-promo-badges[data-product-tags]'))
        ) {
          schedule();
          return;
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-tags'],
  });

  window.injectPromoBadges = injectBadges;
})();
